import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, authPost, getWsToken, logout, forceRedirectToLogin } from './client'
import { getOrCreateDeviceId } from './deviceId' // clearDeviceId called inside logout()
import type {
  Phone, User, CallListResponse, CallStatsResponse,
  AccessKeySummary, AccessKeyDetail, AccessKeyCreated, AccessKeyPlaintext,
} from '../types'
import { useNotify, type NotifyFn } from '../components/Notifications'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  type ApiPhone, apiPhoneToPhone, mergeWithCached,
  setPhoneStatus, handleCallState, handleIncomingCall,
  resetAllPhonesOffline, RELAY_ERRORS,
} from './relay-cache'
import { TIMEOUTS, computeBackoff } from '../config/constants'
import { captureException } from '../lib/sentry'

const RELAY_URL = import.meta.env.VITE_RELAY_URL as string | undefined
if (!RELAY_URL) {
  throw new Error('Missing required environment variable: VITE_RELAY_URL')
}

// --- Auth ---

export function useLogin() {
  return useMutation<{ success: boolean }, Error, { email: string; password: string }>({
    // authPost, NOT the shared request() wrapper: on a 401 request()
    // force-redirects to login and returns a never-resolving promise
    // (client.ts forceRedirectToLogin), so the mutation never settles and a
    // wrong password shows no error / leaves the button stuck. authPost keeps
    // the request timeout (a hung backend rejects instead of hanging the button
    // forever) without that redirect. Mirrors useLoginByKey.
    mutationFn: async (creds: { email: string; password: string }) => {
      let res: Response
      try {
        res = await authPost('/api/v1/auth/login', creds)
      } catch {
        throw new Error('Cannot reach the server')
      }
      if (res.ok) return res.json()
      const body = (await res.json().catch(() => ({}))) as { detail?: unknown }
      // 401 detail is backend-controlled and user-facing ("Invalid email or
      // password" / "Account is deactivated"); 429 from slowapi carries no
      // `detail`, so it's mapped by status.
      if (res.status === 401) {
        throw new Error(typeof body.detail === 'string' ? body.detail : 'Invalid email or password')
      }
      if (res.status === 429) throw new Error('Too many attempts, please wait a minute')
      throw new Error('Something went wrong, please try again')
    },
  })
}

/**
 * Error thrown by useLoginByKey. Carries the HTTP status so the caller can
 * distinguish 401 (invalid key) from 429 (rate-limited) from network errors.
 */
export class LoginKeyError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'LoginKeyError'
    this.status = status
  }
}

/**
 * Log in by access key (operator flow). Bypasses the shared request()
 * wrapper because:
 *   1. On 401 the wrapper force-redirects to login — but we ARE on login
 *      already, so the redirect loops.
 *   2. The caller needs to see the status code to pick a toast (401 vs 429
 *      vs network), which the shared wrapper hides behind Error messages.
 */
export function useLoginByKey() {
  return useMutation<{ success: boolean }, LoginKeyError, string>({
    mutationFn: async (key: string) => {
      let res: Response
      try {
        res = await authPost('/api/v1/auth/login-key', { key })
      } catch {
        throw new LoginKeyError('network', 0)
      }
      if (res.ok) return res.json()
      throw new LoginKeyError(`status_${res.status}`, res.status)
    },
  })
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<User>('/api/v1/auth/me'),
  })
}

// --- Phones ---

export function usePhones() {
  const queryClient = useQueryClient()
  const { data: me } = useMe()
  return useQuery({
    queryKey: ['phones'],
    queryFn: async () => {
      const data = await api.get<ApiPhone[]>('/api/v1/phones')
      const cached = queryClient.getQueryData<Phone[]>(['phones'])
      return data.map(p => mergeWithCached(apiPhoneToPhone(p), cached?.find(c => c.id === p.id)))
    },
    // Admin has no relay WS (AuthLayout strips it — commit 73a9a84), so
    // `phone_status` events never reach their React Query cache. Polling
    // gives them near-live online counters without the WS handshake cost.
    // 10s is the sweet spot: fast enough that an admin watching a fleet
    // notices reconnects, slow enough not to hammer backend. Operators
    // still get live WS pushes and don't need polling.
    refetchInterval: me?.role === 'admin' ? 10_000 : false,
    refetchIntervalInBackground: false,
  })
}

export function usePhone(id: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ['phones', id],
    initialData: () => queryClient.getQueryData<Phone[]>(['phones'])?.find(p => p.id === id),
    queryFn: async () => {
      const data = await api.get<ApiPhone>(`/api/v1/phones/${id}`)
      const cached = queryClient.getQueryData<Phone>(['phones', id])
        || queryClient.getQueryData<Phone[]>(['phones'])?.find(p => p.id === id)
      return mergeWithCached(apiPhoneToPhone(data), cached)
    },
  })
}

// --- Phone mutations ---

export function useCreatePhone() {
  const queryClient = useQueryClient()
  return useMutation({
    // user_id is optional now: admin omits it → backend creates orphan.
    // Operator cannot create phones via this UI anyway (DashboardPage is
    // admin-only), but the type reflects the API contract: `undefined`
    // means "don't set", `null` would be rejected on create.
    mutationFn: (data: { name: string; sim_number: string; user_id?: string }) =>
      api.post<ApiPhone>('/api/v1/phones', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phones'] })
    },
  })
}

export function useUpdatePhone() {
  const queryClient = useQueryClient()
  return useMutation({
    // user_id: admin-only. `undefined` = "don't touch", explicit `null` =
    // orphan (detach from current owner). Operator requests with this
    // field get 403 from PhoneService.update.
    mutationFn: ({ id, ...data }: {
      id: string
      name?: string
      sim_number?: string
      is_active?: boolean
      user_id?: string | null
    }) =>
      api.put<ApiPhone>(`/api/v1/phones/${id}`, data),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: ['phones'] })
      const prev = queryClient.getQueryData<Phone[]>(['phones'])
      if (prev) {
        queryClient.setQueryData<Phone[]>(['phones'], old =>
          old?.map(p => p.id === vars.id ? {
            ...p,
            ...(vars.name !== undefined && { name: vars.name }),
            ...(vars.sim_number !== undefined && { sim: vars.sim_number }),
            ...(vars.is_active !== undefined && { isActive: vars.is_active }),
          } : p)
        )
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['phones'], ctx.prev)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['phones'] })
    },
  })
}

export function useDeletePhone() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/phones/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phones'] })
    },
  })
}

// --- Access keys (admin) ---

const ACCESS_KEYS_QK = ['access-keys'] as const

/**
 * After any access-key mutation the attached-phones set may change,
 * so both lists get invalidated together. Keeping it in one helper
 * keeps the hooks below DRY.
 */
function invalidateAccessKeysAndPhones(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ACCESS_KEYS_QK })
  qc.invalidateQueries({ queryKey: ['phones'] })
}

export function useAccessKeys() {
  const { data: me } = useMe()
  return useQuery({
    queryKey: ACCESS_KEYS_QK,
    queryFn: () => api.get<AccessKeySummary[]>('/api/v1/access-keys'),
    // Same reasoning as usePhones: admin has no WS so phone_status
    // pushes never update `online_count`. 10s polling keeps the list
    // live without a relay subscription. Only admins hit this endpoint
    // (it's 403 for operators), so the gate is defensive not necessary.
    refetchInterval: me?.role === 'admin' ? 10_000 : false,
    refetchIntervalInBackground: false,
  })
}

export function useAccessKey(id: string | null) {
  return useQuery({
    queryKey: ['access-keys', id],
    enabled: id !== null,
    queryFn: () => api.get<AccessKeyDetail>(`/api/v1/access-keys/${id}`),
  })
}

/**
 * Lazily fetch an operator's plaintext key for the reveal affordance.
 * `enabled` is driven by the eye toggle, so the key leaves the server
 * only when an admin explicitly asks. Regenerate invalidates
 * `['access-keys', id]` — a prefix of this key — so a revealed key
 * refetches itself after regeneration.
 */
export function useAccessKeyPlaintext(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['access-keys', id, 'plaintext'],
    enabled,
    queryFn: () => api.get<AccessKeyPlaintext>(`/api/v1/access-keys/${id}/key`),
    staleTime: Infinity,
  })
}

export function useCreateAccessKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { label: string | null; phone_ids: string[] }) =>
      api.post<AccessKeyCreated>('/api/v1/access-keys', data),
    onSuccess: () => invalidateAccessKeysAndPhones(qc),
  })
}

export function useUpdateAccessKeyLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string | null }) =>
      api.patch<AccessKeyDetail>(`/api/v1/access-keys/${id}`, { label }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ACCESS_KEYS_QK })
      qc.invalidateQueries({ queryKey: ['access-keys', vars.id] })
    },
  })
}

export function useRegenerateAccessKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<AccessKeyCreated>(`/api/v1/access-keys/${id}/regenerate`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ACCESS_KEYS_QK })
      qc.invalidateQueries({ queryKey: ['access-keys', id] })
    },
  })
}

export function useDeleteAccessKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/access-keys/${id}`),
    onSuccess: () => invalidateAccessKeysAndPhones(qc),
  })
}

export function useAttachPhone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, phoneId }: { userId: string; phoneId: string }) =>
      api.post<AccessKeyDetail>(
        `/api/v1/access-keys/${userId}/phones`,
        { phone_id: phoneId },
      ),
    onSuccess: (_, vars) => {
      invalidateAccessKeysAndPhones(qc)
      qc.invalidateQueries({ queryKey: ['access-keys', vars.userId] })
    },
  })
}

export function useDetachPhone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, phoneId }: { userId: string; phoneId: string }) =>
      api.delete(`/api/v1/access-keys/${userId}/phones/${phoneId}`),
    onSuccess: (_, vars) => {
      invalidateAccessKeysAndPhones(qc)
      qc.invalidateQueries({ queryKey: ['access-keys', vars.userId] })
    },
  })
}

// --- WebSocket relay ---

// --- Calls ---

export type CallOutcome = 'answered' | 'rejected' | 'cancelled'

export interface CallFilters {
  phoneId?: string
  userId?: string
  dateFrom?: string
  dateTo?: string
  reason?: string
  outcome?: CallOutcome
  search?: string
  page?: number
  pageSize?: number
}

export function useCalls(filters: CallFilters = {}) {
  return useQuery({
    queryKey: ['calls', filters],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters.phoneId) params.set('phone_id', filters.phoneId)
      if (filters.userId) params.set('user_id', filters.userId)
      if (filters.dateFrom) params.set('date_from', filters.dateFrom)
      if (filters.dateTo) params.set('date_to', filters.dateTo)
      if (filters.reason) params.set('reason', filters.reason)
      if (filters.outcome) params.set('outcome', filters.outcome)
      if (filters.search) params.set('search', filters.search)
      if (filters.page) params.set('page', String(filters.page))
      if (filters.pageSize) params.set('page_size', String(filters.pageSize))
      const qs = params.toString()
      return api.get<CallListResponse>(`/api/v1/calls${qs ? `?${qs}` : ''}`)
    },
  })
}

/**
 * Infinite-scroll variant of useCalls — used by the operator
 * CallHistoryPanel where the UX is "show 10, then 'load more'".
 *
 * Shares the queryKey prefix `['calls']` with `useCalls` so existing
 * `qc.invalidateQueries({queryKey:['calls']})` calls (e.g. on
 * call_state:disconnected) refetch BOTH variants. The `infinite: true`
 * marker disambiguates cache slots so the two queries don't collide.
 */
export function useCallsInfinite(
  filters: Omit<CallFilters, 'page'> = {}
) {
  const pageSize = filters.pageSize ?? 10
  return useInfiniteQuery({
    queryKey: ['calls', { ...filters, pageSize, infinite: true }],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams()
      if (filters.phoneId) params.set('phone_id', filters.phoneId)
      if (filters.userId) params.set('user_id', filters.userId)
      if (filters.dateFrom) params.set('date_from', filters.dateFrom)
      if (filters.dateTo) params.set('date_to', filters.dateTo)
      if (filters.reason) params.set('reason', filters.reason)
      if (filters.outcome) params.set('outcome', filters.outcome)
      if (filters.search) params.set('search', filters.search)
      params.set('page', String(pageParam))
      params.set('page_size', String(pageSize))
      return api.get<CallListResponse>(`/api/v1/calls?${params.toString()}`)
    },
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.page_size
      return loaded < lastPage.total ? lastPage.page + 1 : undefined
    },
  })
}

export function useCallStats(filters: Omit<CallFilters, 'page' | 'pageSize' | 'reason' | 'outcome'> = {}) {
  return useQuery({
    queryKey: ['callStats', filters],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters.phoneId) params.set('phone_id', filters.phoneId)
      if (filters.dateFrom) params.set('date_from', filters.dateFrom)
      if (filters.dateTo) params.set('date_to', filters.dateTo)
      const qs = params.toString()
      return api.get<CallStatsResponse>(`/api/v1/calls/stats${qs ? `?${qs}` : ''}`)
    },
  })
}

/**
 * WebSocket connection to Go relay for real-time phone statuses and call events.
 * Writes status/currentCall directly into ['phones'] and ['phones', id] caches.
 * The mergeWithCached() function in queryFn protects these fields from being
 * overwritten by API refetches.
 */
export function useRelay(notify: NotifyFn) {
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const callbacksRef = useRef<RelayCallbacks>({})
  const reconnectAttempt = useRef(0)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const connectTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const [trigger, setTrigger] = useState(0)
  const reconnect = useCallback(() => setTrigger(t => t + 1), [])

  useEffect(() => {
    let active = true
    // Track WebSocket locally — cleanup must only close THIS effect's socket,
    // not one created by a subsequent effect (StrictMode double-mount race).
    let currentWs: WebSocket | null = null

    async function connect() {
      if (!active) return

      let token: string
      try {
        token = await getWsToken()
      } catch (e) {
        // H-8: only network/5xx errors reach this catch. Auth failures
        // (401) go through forceRedirectToLogin() inside request() which
        // returns a pending promise — it never throws, so the user is
        // already navigating to /login. Everything else (TypeError from
        // fetch, 'Request timed out', transient 5xx) is recoverable:
        // schedule a retry using the same backoff as the onclose path.
        // Previously this catch returned silently and the WS stayed dead
        // forever until F5.
        if (!active) return
        const delay = computeBackoff(reconnectAttempt.current)
        reconnectAttempt.current++
        if (import.meta.env.DEV) {
          console.warn(`[relay] ws-token fetch failed, retrying in ${delay}ms`, e)
        }
        reconnectTimer.current = setTimeout(connect, delay)
        return
      }
      if (!active) return

      // Clear any leftover connect timer from previous attempt
      clearTimeout(connectTimer.current)

      const deviceId = getOrCreateDeviceId()
      const ws = new WebSocket(`${RELAY_URL}/ws/browser?token=${token}&device_id=${encodeURIComponent(deviceId)}`)
      currentWs = ws
      wsRef.current = ws

      // WS connection timeout — if handshake doesn't complete, force close and reconnect
      connectTimer.current = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          if (import.meta.env.DEV) console.warn('[relay] connection timeout, closing')
          ws.close()
        }
      }, TIMEOUTS.WS_CONNECTION_MS)

      ws.onopen = () => {
        clearTimeout(connectTimer.current)
        if (import.meta.env.DEV) console.log('[relay] connected to', ws.url)
        // Reconnect (not first connect) → refetch call history. WS-level
        // call_state events may have been missed while we were
        // disconnected; without this the operator sees a stale list
        // until the next user action triggers a refetch (plan §C7).
        if (reconnectAttempt.current > 0) {
          queryClient.invalidateQueries({ queryKey: ['calls'] })
        }
        reconnectAttempt.current = 0
      }

      ws.onmessage = (event) => {
        let msg: Record<string, unknown>
        try {
          msg = JSON.parse(event.data)
        } catch (err) {
          // Without captureException, a regression in relay's wire format
          // (schema/serialization refactor emits malformed JSON) silently
          // drops every frame: call_state never lands → call stuck dialing,
          // phone_status never lands → tile stays stale. DEV-warn branch
          // dead-code-eliminates in prod.
          captureException(
            err instanceof Error ? err : new Error(`relay-ws-parse failed: ${String(err)}`),
            { source: 'relay-ws-parse' },
          )
          if (import.meta.env.DEV) console.error('[relay] invalid JSON:', event.data)
          return
        }

        switch (msg.type) {
          case 'phone_status':
            if (typeof msg.phone_id === 'string' && typeof msg.status === 'string') {
              setPhoneStatus(queryClient, msg.phone_id, msg.status)
            }
            break

          case 'call_state':
            if (typeof msg.call_id === 'string' && typeof msg.state === 'string') {
              handleCallState(queryClient, msg as Parameters<typeof handleCallState>[1])
            }
            break

          case 'incoming_call':
            if (typeof msg.call_id === 'string' && typeof msg.phone_id === 'string' && typeof msg.number === 'string') {
              handleIncomingCall(queryClient, msg as Parameters<typeof handleIncomingCall>[1])
            }
            break

          case 'error': {
            const reason = typeof msg.reason === 'string' ? msg.reason : 'unknown'
            const friendlyMsg = RELAY_ERRORS[reason] || `Relay error: ${reason}`
            notify(friendlyMsg, 'error')
            callbacksRef.current.onError?.(reason)
            break
          }

          case 'webrtc_offer':
            if (typeof msg.call_id === 'string' && typeof msg.sdp === 'string') {
              callbacksRef.current.onWebRTCOffer?.(msg.call_id, msg.sdp)
            }
            break

          case 'ice_candidate':
            if (typeof msg.call_id === 'string' && typeof msg.candidate === 'string') {
              callbacksRef.current.onIceCandidate?.(msg.call_id, msg.candidate)
            }
            break

          case 'session_replaced':
            // R4: kicked by different-device login. Must set active=false
            // BEFORE ws.close() to prevent onclose auto-reconnect timer
            // from firing (which would create a ping-pong session_replaced
            // loop between two tabs — reconnect → kicks other → repeat).
            active = false
            ws.close()
            resetAllPhonesOffline(queryClient)
            // logout() internally calls clearDeviceId() — next login gets fresh UUID
            logout().finally(() => forceRedirectToLogin())
            return

          case 'session_revoked':
            // Admin revoked operator access (phone detach/reattach/
            // deactivate/delete affecting this user). Same cleanup shape
            // as session_replaced — set active=false BEFORE ws.close()
            // to suppress auto-reconnect; the next handshake would just
            // get rejected anyway (kv bump in DB) but the user-visible
            // signal must be the notify here, not a vague disconnect.
            active = false
            ws.close()
            resetAllPhonesOffline(queryClient)
            notify('Доступ отозван администратором', 'error')
            logout().finally(() => forceRedirectToLogin())
            return

          case 'tab_replaced':
            // Bug 1: same-device replacement (Case C in relay/hub.go) — typically
            // a duplicate-tab (Chrome/Firefox copies sessionStorage so two tabs
            // share deviceID). The new tab took over; this tab must stop
            // reconnecting (otherwise it'd kick the new tab → infinite ping-pong).
            // Do NOT logout — auth cookie is shared and the new tab is using it.
            active = false
            ws.close()
            notify('Сессия перенесена в новую вкладку', 'error')
            return
        }
      }

      ws.onclose = () => {
        if (import.meta.env.DEV) console.log('[relay] disconnected')
        if (active) {
          const delay = computeBackoff(reconnectAttempt.current)
          if (import.meta.env.DEV) console.log(`[relay] reconnecting in ${delay}ms (attempt ${reconnectAttempt.current + 1})`)
          reconnectAttempt.current++
          reconnectTimer.current = setTimeout(connect, delay)
        }
      }

      ws.onerror = (err) => {
        if (import.meta.env.DEV) console.error('[relay] error:', err)
      }
    }

    connect()

    return () => {
      active = false
      clearTimeout(connectTimer.current)
      clearTimeout(reconnectTimer.current)
      // Close only THIS effect's socket — not a newer one from a remount
      currentWs?.close()
    }
  }, [queryClient, notify, trigger, reconnect])

  return { wsRef, callbacksRef }
}

/** Callbacks that page components can register to handle relay signaling messages. */
export interface RelayCallbacks {
  onWebRTCOffer?: (callId: string, sdp: string) => void
  onIceCandidate?: (callId: string, candidate: string) => void
  onError?: (reason: string) => void
}

interface RelayContextValue {
  wsRef: React.RefObject<WebSocket | null>
  callbacksRef: React.RefObject<RelayCallbacks>
}

/** Context for sharing the relay WebSocket send capability with child components. */
export const RelayContext = createContext<RelayContextValue>({
  wsRef: { current: null },
  callbacksRef: { current: {} },
})

/** Send a JSON message through the relay WebSocket. Notifies user if disconnected. */
export function useRelaySend() {
  const { wsRef } = useContext(RelayContext)
  const notify = useNotify()
  return (msg: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg))
    } else {
      notify('Not connected to relay server', 'error')
    }
  }
}

/** Register WebRTC signaling callbacks from a page component. Cleans up on unmount. */
export function useRelayCallbacks(callbacks: Partial<RelayCallbacks>) {
  const { callbacksRef } = useContext(RelayContext)
  useEffect(() => {
    const ref = callbacksRef.current
    Object.assign(ref, callbacks)
    return () => {
      for (const key of Object.keys(callbacks) as (keyof RelayCallbacks)[]) {
        delete ref[key]
      }
    }
  // Callbacks identity doesn't change — registered once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbacksRef])
}

